import Vue from 'vue';
import VueI18n from 'vue-i18n';
import pt from './pt.json';
import en from './en.json';
import backendTextsEn from './backend-en.json';
import backendHtmlEn from './backend-html-en.json';
import backendUrlsEn from './backend-urls-en.json';

Vue.use(VueI18n);

const backendTexts = { en: backendTextsEn };

export const SUPPORTED_LOCALES = ['pt', 'en'];
export const DEFAULT_LOCALE = 'pt';

// Missing keys in `en` fall back to `pt`, so pages can be migrated in phases.
export const i18n = new VueI18n({
    locale: DEFAULT_LOCALE,
    fallbackLocale: DEFAULT_LOCALE,
    silentFallbackWarn: true,
    messages: { pt, en },
});

let vuetify = null;

export function bindVuetify(instance) {
    vuetify = instance;
}

export function setLocale(locale) {
    const target = SUPPORTED_LOCALES.includes(locale) ? locale : DEFAULT_LOCALE;

    i18n.locale = target;
    if (vuetify) vuetify.framework.lang.current = target;
    document.documentElement.setAttribute('lang', target);
}

// Texts defined in the backend (datatable headers, filter schemas, ...) come in pt. This maps them to the current locale,
// falling back to the original text when there is no translation.
export function tb(text) {
    if (typeof text !== 'string' || i18n.locale === DEFAULT_LOCALE) return text;

    const dictionary = backendTexts[i18n.locale];

    // Month labels formatted by the backend (e.g. 'Fev 2026')
    const month = text.match(/^([A-Za-zç]{3})( \d{4})?$/);
    if (month && dictionary?.[month[1]] && !dictionary?.[text]) return dictionary[month[1]] + (month[2] ?? '');

    // Backend texts may carry stray whitespace (e.g. 'Campanhas ' in config_custom_shortcuts)
    return dictionary?.[text] ?? dictionary?.[text.trim()] ?? text;
}

const TRANSLATABLE_SCHEMA_KEYS = ['label', 'placeholder', 'hint', 'noDataText', 'title'];

export function translateHeaders(headers) {
    return Array.isArray(headers) ? headers.map((header) => ({ ...header, text: tb(header.text) })) : headers;
}

function translateField(field) {
    if (!field || typeof field !== 'object' || Array.isArray(field)) return field;

    const translated = { ...field };

    // Select items given as plain strings: show the translated text but keep the original string as the value
    // (itemText/itemValue are meaningless for string items, so they can be overridden)
    if (Array.isArray(field.items) && field.items.length && field.items.every((item) => typeof item === 'string') && !field.returnObject) {
        translated.items = field.items.map((item) => ({ text: tb(item), value: item }));
        translated.itemText = 'text';
        translated.itemValue = 'value';
    }

    // Select items given as objects whose text is shown through `itemText` (the value is derived from that same text):
    // keep the original text as the value and translate only what is displayed
    const textKey = field.itemText;
    if (typeof textKey === 'string' && Array.isArray(field.items) && field.items.length && !field.returnObject && !field.itemValue && field.items.every((item) => item && typeof item === 'object' && typeof item[textKey] === 'string' && item.value === undefined)) {
        translated.items = field.items.map((item) => ({ ...item, value: item[textKey], [textKey]: tb(item[textKey]) }));
        translated.itemValue = 'value';
    }
    TRANSLATABLE_SCHEMA_KEYS.forEach((prop) => {
        if (typeof translated[prop] === 'string') translated[prop] = tb(translated[prop]);
    });

    return translated;
}

export function translateSchema(schema) {
    if (!schema || typeof schema !== 'object') return schema;
    if (Array.isArray(schema)) return schema.map(translateField);

    return Object.fromEntries(Object.entries(schema).map(([key, field]) => [key, translateField(field)]));
}

// Notifications are created (and stored) in pt by the backend. Titles look like '(3058.0): <message>' and subtitles like '28-07-2026 11:58 - Por: <name>'.
const NOTIFICATION_PATTERNS = [
    [/^Conta corrente: (.+) - Nova ref\. de pagamento\.$/, 'notifications.checkingAccountPayment'],
    [/^Pagamento do documento (.+) aceite\.$/, 'notifications.paymentAccepted'],
    [/^Criada ref\. de pagamento do documento (.+)\.$/, 'notifications.paymentRefCreated'],
    [/^Alerta de encomenda pendente \((.+)\)\.$/, 'notifications.pendingOrderAlert'],
];

export function translateNotificationTitle(text) {
    if (typeof text !== 'string' || i18n.locale === DEFAULT_LOCALE) return text;

    const [, prefix = '', body] = text.match(/^(\([^)]*\):\s*)?([\s\S]*)$/);
    for (const [pattern, key] of NOTIFICATION_PATTERNS) {
        const match = body.match(pattern);
        if (match) return prefix + i18n.t(key, { doc: match[1] });
    }

    return prefix + tb(body);
}

export function translateNotificationSubtitle(text) {
    if (typeof text !== 'string' || i18n.locale === DEFAULT_LOCALE) return text;

    const match = text.match(/^(.*? - )Por: ([\s\S]*)$/);

    return match ? `${match[1]}${i18n.t('notifications.by')}: ${tb(match[2])}` : text;
}

// HTML written in the backoffice (pt): translates the known sentences it contains and leaves anything else untouched.
export function translateHtml(html) {
    if (typeof html !== 'string' || i18n.locale === DEFAULT_LOCALE) return html;

    const fragments = { en: backendHtmlEn }[i18n.locale] ?? {};

    return Object.entries(fragments)
        .sort(([a], [b]) => b.length - a.length)
        .reduce((result, [pt, translated]) => result.split(pt).join(translated), html);
}

// Links stored in the backoffice point to the pt version of the website. Returns the page for the current language when one is known.
export function translateUrl(url) {
    if (typeof url !== 'string' || i18n.locale === DEFAULT_LOCALE) return url;

    const urls = { en: backendUrlsEn }[i18n.locale] ?? {};

    return urls[url] ?? url;
}

// Language chosen on the public pages (login, register, ...), where there is no user yet to store it on.
const PUBLIC_LOCALE_KEY = 'myprovet_public_locale';

export function getPublicLocale() {
    try {
        const stored = window.localStorage.getItem(PUBLIC_LOCALE_KEY);

        return SUPPORTED_LOCALES.includes(stored) ? stored : DEFAULT_LOCALE;
    } catch (e) {
        return DEFAULT_LOCALE;
    }
}

export function setPublicLocale(locale) {
    try {
        window.localStorage.setItem(PUBLIC_LOCALE_KEY, locale);
    } catch (e) {
        // Storage can be unavailable (private mode, blocked site data): the choice then only lasts until the page is reloaded
    }

    setLocale(locale);
}

// Messages the server sends in pt (validation, authentication, password reset). Exact matches come from the dictionary,
// the Laravel validation templates are matched by pattern and their attribute name is translated separately.
const SERVER_MESSAGE_PATTERNS = [
    [/^O campo (.+) é obrigatório\.$/, 'serverMessages.required'],
    [/^O campo (.+) deve ser um endereço de email válido\.$/, 'serverMessages.email'],
    [/^O campo (.+) já está a ser utilizado\.$/, 'serverMessages.unique'],
    [/^O campo confirmação da (.+) não é valido\.$/, 'serverMessages.confirmed'],
    [/^O campo (.+) deve ter pelo menos (\d+) caracteres\.$/, 'serverMessages.min'],
    [/^O campo (.+) não pode ser superior a (\d+) caracteres\.$/, 'serverMessages.max'],
    [/^O campo (.+) tem um formato inválido\.$/, 'serverMessages.format'],
    [/^O campo (.+) selecionado é inválido\.$/, 'serverMessages.selected'],
    [/^O campo (.+) deve ser aceite\.$/, 'serverMessages.accepted'],
    [/^O campo (.+) deve ser um número\.$/, 'serverMessages.numeric'],
    [/^O campo (.+) deve ter (\d+) dígitos\.$/, 'serverMessages.digits'],
    [/^Demasiadas tentativas\. Por favor tente outra vez em (\d+) segundos\.$/, 'serverMessages.throttle'],
];

export function translateServerMessage(text) {
    if (typeof text !== 'string' || i18n.locale === DEFAULT_LOCALE) return text;

    const exact = tb(text);
    if (exact !== text) return exact;

    for (const [pattern, key] of SERVER_MESSAGE_PATTERNS) {
        const match = text.match(pattern);
        if (match) return i18n.t(key, { attr: tb(match[1]), n: match[2] ?? match[1], seconds: match[1] });
    }

    return text;
}
